import { addSeconds } from 'date-fns';
import { chain, isEmpty, isNil } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { Injectable } from "@angular/core";
import { TOKEN_KEY, SSO_SUCCESS_KEY, TOKEN_DATA_KEY, REFRESHTOKEN_KEY, USER_KEY, REMEMBER_KEY } from '../constants';
import { LoginResponse } from '../models';
import * as _ from 'lodash';

export type TokenInfo = Partial<LoginResponse>;

@Injectable({
	providedIn: 'root'
})
export class TokenStorageService {

	constructor(private _cookieService: CookieService) {
	}

	public clearStorageKeys(args: { keysToOmit?: string[] } = {}): void {
		if (args.keysToOmit.length) {
			const keys: string[] = chain(Object.keys(localStorage))
				.omitBy(key => args.keysToOmit.includes(key))
				.values()
				.value();

			keys.forEach(key => args.keysToOmit.includes(key) ? localStorage.removeItem(key) : null);
		} else {
			window.localStorage.clear();
		}
		this._cookieService.delete(TOKEN_KEY, '/', undefined);
	}

	public saveToken(tokenInfo: TokenInfo): void {
		// console.log(tokenInfo)
		const token = tokenInfo.access_token;
		// console.log(token)
		if (tokenInfo.remember || this.hasSavedAuthentication()) {
			window.localStorage.setItem(TOKEN_DATA_KEY, token);
		} else {
			this.setTokenTimeExpiration(tokenInfo);
		}
		const user = this.getUser();
		if (user.id) {
			this.saveUser({ ...user, accessToken: token });
		}
		this.saveAuthData(tokenInfo);
		this.saveRefreshToken(tokenInfo.refreshToken);
	}

	public setTokenTimeExpiration(tokenInfo: TokenInfo) {
		// console.log(tokenInfo)
		const token = tokenInfo.access_token;
		// console.log(token)
		this._cookieService.set(TOKEN_KEY, token, addSeconds(new Date(), tokenInfo.expiresIn), '/', undefined, false, 'Lax');
	}

	public renewTokenTimeExpiration(): void {
		const tokenInfoJson = this.getAuthData();
		if (tokenInfoJson) {
			const tokenInfo = { ...tokenInfoJson, remember: false };
			this.setTokenTimeExpiration(tokenInfo);
		}
	}

	public removeSsoAuthFromCookie(): void {
        const currentDomain = window.location.hostname;
        console.log(`Removing SSO auth cookies for domain: ${currentDomain}`);
        this._cookieService.delete(SSO_SUCCESS_KEY, '/', currentDomain);
        this._cookieService.delete(SSO_SUCCESS_KEY, '/', `.${currentDomain}`);
	}

	public getToken(): string | null {
		var value = this.hasSavedAuthentication() ?
			window.localStorage.getItem(TOKEN_DATA_KEY) :
			(isNil(this._cookieService.get(TOKEN_KEY))
				? this._cookieService.get(TOKEN_KEY)
				: window.localStorage.getItem(TOKEN_DATA_KEY));
		value = value && this.isJSONString(value) ? JSON.parse(value).access_token : value;
		if (isEmpty(value) || value === 'null' || value === 'undefined' || value === 'Bearer undefined') return null;

		return value;
	}

	public getAuthData(): LoginResponse {
		const authDataToParse = window.localStorage.getItem(TOKEN_DATA_KEY);

		if (isEmpty(authDataToParse) || authDataToParse === 'null' || authDataToParse === 'undefined') return null;

		return JSON.parse(authDataToParse) as LoginResponse;
	}

	public hasSavedAuthentication(): boolean {
		return !!JSON.parse(window.localStorage.getItem(REMEMBER_KEY));
	}

	public saveRefreshToken(token: string): void {
		window.localStorage.removeItem(REFRESHTOKEN_KEY);
		window.localStorage.setItem(REFRESHTOKEN_KEY, token);
	}

	public saveAuthData(authData: TokenInfo): void {
		window.localStorage.removeItem(TOKEN_DATA_KEY);
		window.localStorage.setItem(TOKEN_DATA_KEY, JSON.stringify(authData));
	}

	public getRefreshToken(): string | null {
		return window.localStorage.getItem(REFRESHTOKEN_KEY);
	}

	public saveUser(user: any): void {
		window.localStorage.removeItem(USER_KEY);
		window.localStorage.setItem(USER_KEY, JSON.stringify(user));
	}

	public getUser(): any {
		const userToParse = window.localStorage.getItem(USER_KEY);
		if (isEmpty(userToParse) || userToParse === 'null' || userToParse === 'undefined') return {};

		return JSON.parse(userToParse);
	}

	public isJSONString(str: string): boolean {
		try {
			const parsed = _.attempt(JSON.parse.bind(null, str));
			return !_.isError(parsed);
		} catch (error) {
			return false;
		}
	}
}
